import type { Abbrev } from '../types';

/**
 * What a caregiver's abbreviation has to satisfy before it is saved (`AUT-09`).
 *
 * `PRD-16` holds the six seeded codes to these rules and a unit test asserts it; nothing has ever
 * held a caregiver's code to them. The panel wrote straight through `abbrevRepo.upsert`, which is
 * keyed on the code — so `gwa` typed twice replaced the seeded sentence with no word about it, and
 * the person's muscle memory then said something they did not mean.
 *
 * The prefix rule is the one that matters most. An expansion fires on a word boundary
 * (`core/prediction/abbrev.ts`), so a code that opens a real word turns that word into somebody
 * else's sentence at the next space — and the person has to notice it, undo it and retype, which is
 * exactly the surprise §5.3b says is worse than no expansion at all.
 *
 * Pure. The word list is passed in rather than read here: the packed lexicon lives in a worker and
 * may not be touched from the main thread, so what this can check is the core vocabulary and
 * whatever else the caller can hand it.
 */

export type AbbrevProblem =
  /** No code, or nothing to expand to. */
  | 'empty'
  /** The code is not shorter than its expansion, so it saves nothing. */
  | 'notShorter'
  /** Something already answers to this code. */
  | 'duplicate'
  /** The code opens — or is — a word the person might be spelling. */
  | 'prefixOfWord';

export function checkAbbrev(
  candidate: Abbrev,
  existing: readonly Abbrev[],
  words: readonly string[],
): AbbrevProblem | null {
  const code = candidate.code.trim().toLowerCase();
  const expansion = candidate.expansion.trim();

  if (code === '' || expansion === '') return 'empty';
  if (code.length >= expansion.length) return 'notShorter';
  if (existing.some((abbrev) => abbrev.code.trim().toLowerCase() === code)) return 'duplicate';

  // `startsWith` rather than equality, because both are the same failure: one certain, one waiting
  // for the person to type a longer word that begins the same way.
  if (words.some((word) => word.trim().toLowerCase().startsWith(code))) return 'prefixOfWord';

  return null;
}
